import React, { useState, useEffect, useRef } from 'react';
import { Box, Typography, Grid, Container } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import CommonButton from '../../common/components/CommonButton';
import { useNavigate } from 'react-router-dom';
import { CarouselItems } from '../../common/const';
import AboutUs from './AboutUs';
import Feature from './Feature';
import Testimonial from './Testimonial';
import Blogs from './Blogs';
import Faq from './Faq';
import Footer from './Footer';
import { East } from '@mui/icons-material';
import Navbar from './Navbar';

const AnimatedNumber = ({
  value,
  duration = 2000,
  color
}) => {
  const [displayValue, setDisplayValue] = useState(0);
  const animationRef = useRef(null);
  const startTimeRef = useRef(null);
  const hasAnimated = useRef(false);

  const parseValue = (str) => {
    if (typeof str !== 'string') return Number(str) || 0;
    const numericString = str.replace(/[^0-9.]/g, '');
    const parsed = parseFloat(numericString);
    return isNaN(parsed) ? 0 : parsed;
  };

  useEffect(() => {
    const targetValue = parseValue(value);
    const startValue = hasAnimated.current ? displayValue : 0;

    if (animationRef.current) {
      cancelAnimationFrame(animationRef.current);
    }

    startTimeRef.current = Date.now();

    const animate = () => {
      const now = Date.now();
      const elapsed = now - startTimeRef.current;
      const progress = Math.min(elapsed / duration, 1);
      const easeOut = 1 - Math.pow(1 - progress, 3);
      const currentValue = startValue + ((targetValue - startValue) * easeOut);

      setDisplayValue(Math.round(currentValue));

      if (progress < 1) {
        animationRef.current = requestAnimationFrame(animate);
      } else {
        hasAnimated.current = true;
      }
    };

    animationRef.current = requestAnimationFrame(animate);

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [value, duration]);

  const suffix = typeof value === 'string' ? value.match(/[+%]/g)?.[0] || '' : '';

  return (
    <Typography
      component="span"
      sx={{
        color: color,
        fontSize: { xs: '28px', sm: '32px', md: '36px', lg: '40px' },
        fontWeight: 'bold',
        lineHeight: 1.2
      }}
    >
      {displayValue.toLocaleString()}{suffix}
    </Typography>
  );
};

const LandingPage = () => {
  const navigate = useNavigate();
  const [activeIndex, setActiveIndex] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);
  const theme = useTheme();
  const timeoutRef = useRef(null);
  const homeRef = useRef(null);
  const aboutRef = useRef(null);
  const featureRef = useRef(null);
  const blogsRef = useRef(null);
  const testimonialRef = useRef(null);

  const scrollToSection = (sectionRef) => {
    if (sectionRef.current) {
      sectionRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'start'
      });
    }
  };

  const handleLinkClick = (e, sectionRef) => {
    e.preventDefault();
    scrollToSection(sectionRef);
  };

  useEffect(() => {
    const interval = setInterval(() => {
      setIsAnimating(true);
      setTimeout(() => {
        setActiveIndex((prev) => (prev + 1) % CarouselItems.length);
        setIsAnimating(false);
      }, 800);
    }, 5000);

    return () => clearInterval(interval);
  }, []);

  const handleSlideChange = (index) => {
    if (index === activeIndex || isAnimating) return;
    setIsAnimating(true);
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(() => {
      setActiveIndex(index);
      setIsAnimating(false);
    }, 400);
  };

  return (
    <Box sx={{ overflowX: 'hidden' }}>
      {/* HERO SECTION */}
      <Box
        id="home"
        ref={homeRef}
        sx={{
          minHeight: { xs: '100vh', sm: '110vh' },
          height: 'auto',
          width: '100%',
          position: 'relative',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {/* Background Image Container */}
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            zIndex: 1,
            backgroundImage: `url(${CarouselItems[activeIndex].background})`,
            backgroundPosition: 'center',
            backgroundSize: 'cover',
            backgroundRepeat: 'no-repeat'
          }}
        />

        {/* Content Container */}
        <Container
          maxWidth="xl"
          sx={{
            position: 'relative',
            zIndex: 3,
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            flex: 1,
            px: { xs: 2, sm: 3, md: 4 }
          }}
        >
          {/* NAVBAR */}
          <Navbar
            handleLinkClick={handleLinkClick}
            homeRef={homeRef}
            aboutRef={aboutRef}
            blogsRef={blogsRef}
            featureRef={featureRef}
            testimonialRef={testimonialRef}
            fromLanding={true}
          />

          {/* Hero Content Wrapper */}
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              flex: 1,
              py: { xs: 4, sm: 5, md: 3 },
              mt: { xs: 0, sm: 2, md: 0 },
              pb: { xs: 10, sm: 12, md: 14 } // Increased padding bottom for indicators
            }}
          >
            {/* Chip */}
            <Typography
              sx={{
                background: theme.palette.primary.main,
                padding: { xs: '4px 12px', sm: '2px 10px' },
                borderRadius: 1,
                fontWeight: 600,
                color: theme.palette.secondary.main,
                fontSize: { xs: '14px', sm: '15px', md: '17px' },
                display: 'inline-block',
                mb: { xs: 2, sm: 3, md: 4 }
              }}
            >
              {CarouselItems[activeIndex].title}
            </Typography>

            {/* Subtitle */}
            <Typography
              sx={{
                color: CarouselItems[activeIndex].headingColor,
                fontSize: {
                  xs: '28px',
                  sm: '36px',
                  md: '45px',
                  lg: '55px'
                },
                width: {
                  xs: '95%',
                  sm: '85%',
                  md: '75%',
                  lg: '65%'
                },
                textAlign: 'center',
                fontWeight: 600,
                lineHeight: { xs: 1.3, sm: 1.2, md: 1.1 },
                mb: { xs: 2, sm: 2.5, md: 3 }
              }}
            >
              {CarouselItems[activeIndex].subtitle}
            </Typography>

            {/* Description */}
            <Typography
              variant="body1"
              sx={{
                color: CarouselItems[activeIndex].headingColor,
                fontSize: {
                  xs: '14px',
                  sm: '15px',
                  md: '16px',
                  lg: '17px'
                },
                width: {
                  xs: '95%',
                  sm: '80%',
                  md: '60%',
                  lg: '45%'
                },
                textAlign: 'center',
                fontWeight: 500,
                mb: { xs: 3, sm: 3.5, md: 4 },
                opacity: 0.95
              }}
            >
              {CarouselItems[activeIndex].description}
            </Typography>

            {/* Buttons */}
            <Box
              sx={{
                display: 'flex',
                gap: { xs: 1.5, sm: 2 },
                mb: { xs: 4, sm: 5, md: 6 },
                justifyContent: 'center',
                flexDirection: { xs: 'column', sm: 'row' },
                width: { xs: '100%', sm: 'auto' },
                px: { xs: 2, sm: 0 }
              }}
            >
              <CommonButton
                label="Start Planning With AI"
                endIcon={<East />}
                background="White"
                fontWeight={600}
                width={{ xs: '100%', sm: '220px', md: '250px' }}
                height={{ xs: '44px', sm: '45px' }}
                fontSize={{ xs: '15px', sm: '16px', md: '17px' }}
                color="black"
                handleClick={() => navigate('/login')}
              />
              <CommonButton
                label="Explore College Planner"
                endIcon={<East />}
                background="Transparent"
                fontWeight={600}
                width={{ xs: '100%', sm: '220px', md: '280px' }}
                height={{ xs: '44px', sm: '45px' }}
                color="white"
                fontSize={{ xs: '15px', sm: '16px', md: '17px' }}
                border="1px solid white"
                handleClick={(e) => handleLinkClick(e, featureRef)}
                sx={{
                  '&:hover': {
                    backgroundColor: 'rgba(255, 255, 255, 0.1)'
                  }
                }}
              />
            </Box>

            {/* Stats */}
            <Grid
              container
              spacing={{ xs: 2, sm: 3 }}
              sx={{
                maxWidth: {
                  xs: '100%',
                  sm: '80%',
                  md: '60%',
                  lg: '40%'
                },
                px: { xs: 2, sm: 0 }
              }}
            >
              {CarouselItems[activeIndex].stats.map((stat, index) => (
                <Grid item xs={12} sm={4} key={index}>
                  <Box sx={{ 
                    textAlign: 'center',
                    px: { xs: 1, sm: 0 }
                  }}>
                    <AnimatedNumber
                      value={stat.value}
                      color={CarouselItems[activeIndex].headingColor}
                      duration={2000}
                    />
                    <Typography
                      variant="body2"
                      sx={{
                        color: CarouselItems[activeIndex].headingColor,
                        fontSize: {
                          xs: '0.875rem',
                          sm: '0.9rem',
                          md: '1rem'
                        },
                        fontWeight: 500,
                        mt: 0.5,
                        whiteSpace: { xs: 'normal', sm: 'nowrap' }
                      }}
                    >
                      {stat.label}
                    </Typography>
                  </Box>
                </Grid>
              ))}
            </Grid>
          </Box>
        </Container>

        {/* Indicators - Restored original design with fixed spacing */}
        <Box
          sx={{
            position: 'absolute',
            bottom: { xs: 45, sm: 50, md: 60 }, // Increased bottom spacing
            left: '50%',
            transform: 'translateX(-50%)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 20,
            px: { xs: 2, sm: 3 },
            py: { xs: 1, sm: 1.5 }
          }}
        >
          <Box
            sx={{
              position: 'relative',
              display: 'flex',
              gap: { xs: 1.5, sm: 2 },
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            {/* Animated indicator dot - exactly as original */}
            <Box
              sx={{
                position: 'absolute',
                left: 0,
                width: { xs: 12, sm: 14, md: 15 },
                height: { xs: 12, sm: 14, md: 15 },
                borderRadius: '50%',
                backgroundColor: CarouselItems[activeIndex].indicatorColor,
                transform: `translateX(${activeIndex * (28)}px)`,
                transition: 'transform 0.6s cubic-bezier(0.34, 1.56, 0.64, 1)',
                zIndex: 1,
                display: { xs: 'none', sm: 'block' } // Hide on mobile as in original
              }}
            />

            {/* Static dots - exactly as original */}
            {CarouselItems.map((_, index) => {
              const isActive = index === activeIndex;

              return (
                <Box
                  key={index}
                  onClick={() => handleSlideChange(index)}
                  sx={{
                    position: 'relative',
                    width: { xs: 10, sm: 13, md: 15 },
                    height: { xs: 10, sm: 13, md: 15 },
                    borderRadius: '50%',
                    backgroundColor: 'transparent',
                    border: `2px solid ${isActive ? 'transparent' : 'rgba(255, 255, 255, 0.7)'}`,
                    cursor: isAnimating ? 'not-allowed' : 'pointer',
                    zIndex: 2,
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      backgroundColor: isAnimating ? 'transparent' : `${CarouselItems[activeIndex].indicatorColor}40`,
                      transform: isAnimating ? 'scale(1)' : 'scale(1.2)',
                    },
                  }}
                />
              );
            })}
          </Box>
        </Box>
      </Box>

      {/* OTHER SECTIONS */}
      <AboutUs aboutRef={aboutRef} />
      <Feature featureRef={featureRef} />
      <Testimonial testimonialRef={testimonialRef} />
      <Blogs blogsRef={blogsRef} />
      <Faq />
      <Footer />
    </Box>
  );
};

export default LandingPage;